import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import {
  TrendingUp, TrendingDown, IndianRupee, CalendarCheck, Gauge, Clock, Timer, Sun, Moon,
  Ticket, Zap, ChevronDown, X, RotateCcw, Filter, Lightbulb, Target, Trophy, AlertTriangle,
} from 'lucide-react';
import { BACKEND_URL, API } from '../utils/apiConfig';

const DATE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This Week' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'mtd', label: 'Month to Date' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'custom', label: 'Custom' },
];

const BOOKING_TYPES = [
  { value: 'all', label: 'All (incl. Bulk)' },
  { value: 'exclude_bulk', label: 'Exclude Bulk' },
  { value: 'include_bulk', label: 'Include Bulk' },
  { value: 'bulk_only', label: 'Bulk Only' },
];

const DAYS = [
  { value: 'monday', label: 'Mon' }, { value: 'tuesday', label: 'Tue' },
  { value: 'wednesday', label: 'Wed' }, { value: 'thursday', label: 'Thu' },
  { value: 'friday', label: 'Fri' }, { value: 'saturday', label: 'Sat' },
  { value: 'sunday', label: 'Sun' },
];

const DEFAULTS = {
  preset: 'this_month', date_from: '', date_to: '',
  venues: [], games: [], days: [], courts: [], platforms: [],
  time_start: '', time_end: '', booking_type: 'exclude_bulk',
  peak_start: '18:00', peak_end: '23:00',
};

// ---- formatters ----
const fmtK = (n) => {
  const v = Number(n) || 0;
  const abs = Math.abs(v);
  if (abs >= 10000000) return `${(v / 10000000).toFixed(2)}Cr`;
  if (abs >= 100000) return `${(v / 100000).toFixed(2)}L`;
  if (abs >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return `${Math.round(v)}`;
};
const inr = (n) => `₹${fmtK(n)}`;
const inrFull = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN')}`;

// ---- multi-select ----
const MultiSelect = ({ label, options, selected, onChange, testid, allLabel = 'All' }) => {
  const toggle = (v) => {
    if (selected.includes(v)) onChange(selected.filter((x) => x !== v));
    else onChange([...selected, v]);
  };
  const summary = selected.length === 0 ? allLabel
    : selected.length === 1 ? selected[0]
    : `${selected.length} selected`;
  return (
    <div className="min-w-[150px]">
      <Label className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <button
            data-testid={`mgmt-filter-${testid}`}
            className="mt-1 w-full flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:border-emerald-400 transition-colors"
          >
            <span className="truncate">{summary}</span>
            <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-60 p-2 max-h-72 overflow-y-auto" align="start">
          {options.length === 0 && <p className="text-xs text-slate-400 px-2 py-1">No options</p>}
          {options.map((o) => (
            <label key={o} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm">
              <Checkbox checked={selected.includes(o)} onCheckedChange={() => toggle(o)} data-testid={`mgmt-opt-${testid}-${o}`} />
              <span className="truncate">{o}</span>
            </label>
          ))}
        </PopoverContent>
      </Popover>
    </div>
  );
};

// ---- change pill ----
const ChangePill = ({ value, invert = false }) => {
  if (value === null || value === undefined) return <span className="text-xs text-slate-400" data-testid="mgmt-change-na">N/A</span>;
  const up = value >= 0;
  const good = invert ? !up : up;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${good ? 'text-emerald-600' : 'text-rose-600'}`}>
      <Icon className="w-3 h-3" />{Math.abs(value)}%
    </span>
  );
};

const KpiCard = ({ icon: Icon, title, value, prev, change, accent = 'emerald', invert = false }) => (
  <Card className="p-4 relative overflow-hidden bg-white border-slate-200" data-testid={`mgmt-kpi-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
    <div className="flex items-start justify-between">
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold truncate">{title}</p>
        <p className="text-2xl font-bold text-slate-900 mt-1 truncate">{value}</p>
        <div className="flex items-center gap-2 mt-1">
          <ChangePill value={change} invert={invert} />
          <span className="text-[11px] text-slate-400">prev {prev}</span>
        </div>
      </div>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center bg-${accent}-50 shrink-0`}>
        <Icon className={`w-4 h-4 text-${accent}-600`} />
      </div>
    </div>
  </Card>
);

const SectionTitle = ({ children, sub }) => (
  <div className="mb-3">
    <h3 className="font-bold text-slate-900 text-base">{children}</h3>
    {sub && <p className="text-xs text-slate-400">{sub}</p>}
  </div>
);

const Th = ({ children, right }) => <th className={`py-2 px-2 font-semibold text-slate-500 text-xs uppercase tracking-wide ${right ? 'text-right' : 'text-left'}`}>{children}</th>;
const Td = ({ children, right, bold }) => <td className={`py-2 px-2 ${right ? 'text-right' : 'text-left'} ${bold ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>{children}</td>;

export default function MgmtDashboardPage() {
  const [opts, setOpts] = useState({ venues: [], games: [], platforms: [] });
  const [draft, setDraft] = useState(DEFAULTS);
  const [applied, setApplied] = useState(DEFAULTS);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [trendMetric, setTrendMetric] = useState('revenue');

  const token = localStorage.getItem('token');
  const authH = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  // court options depend on selected venues (draft)
  const courtOptions = useMemo(() => {
    const vs = opts.venues || [];
    const chosen = draft.venues.length ? vs.filter((v) => draft.venues.includes(v.name)) : vs;
    const set = new Set();
    chosen.forEach((v) => (v.courts || []).forEach((c) => set.add(c)));
    return Array.from(set).sort();
  }, [opts, draft.venues]);

  useEffect(() => {
    axios.get(`${API}/mgmt/filter-options`, authH)
      .then((r) => setOpts(r.data))
      .catch(() => toast.error('Failed to load filter options'));
  }, [authH]);

  const buildParams = useCallback((f) => {
    const p = new URLSearchParams();
    p.set('preset', f.preset);
    if (f.preset === 'custom') {
      if (f.date_from) p.set('date_from', f.date_from);
      if (f.date_to) p.set('date_to', f.date_to);
    }
    if (f.venues.length) p.set('venues', f.venues.join(','));
    if (f.games.length) p.set('games', f.games.join(','));
    if (f.days.length) p.set('days', f.days.join(','));
    if (f.courts.length) p.set('courts', f.courts.join(','));
    if (f.platforms.length) p.set('platforms', f.platforms.join(','));
    if (f.time_start && f.time_end) { p.set('time_start', f.time_start); p.set('time_end', f.time_end); }
    p.set('booking_type', f.booking_type);
    p.set('peak_start', f.peak_start);
    p.set('peak_end', f.peak_end);
    return p.toString();
  }, []);

  const fetchData = useCallback((f) => {
    setLoading(true);
    axios.get(`${API}/mgmt/overview?${buildParams(f)}`, authH)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [authH, buildParams]);

  useEffect(() => { fetchData(DEFAULTS); }, [fetchData]);

  const apply = () => { setApplied(draft); fetchData(draft); };
  const clearAll = () => { setDraft(DEFAULTS); setApplied(DEFAULTS); fetchData(DEFAULTS); };

  // active filter chips (from applied)
  const chips = useMemo(() => {
    const c = [];
    const presetLabel = DATE_PRESETS.find((d) => d.value === applied.preset)?.label;
    if (applied.preset === 'custom' && applied.date_from && applied.date_to) c.push({ k: 'preset', label: `${applied.date_from} → ${applied.date_to}` });
    else c.push({ k: 'preset', label: presetLabel });
    applied.venues.forEach((v) => c.push({ k: 'venues', v, label: `Venue: ${v}` }));
    applied.games.forEach((v) => c.push({ k: 'games', v, label: `Game: ${v}` }));
    applied.days.forEach((v) => c.push({ k: 'days', v, label: DAYS.find((d) => d.value === v)?.label }));
    applied.courts.forEach((v) => c.push({ k: 'courts', v, label: `Court: ${v}` }));
    applied.platforms.forEach((v) => c.push({ k: 'platforms', v, label: v }));
    if (applied.time_start && applied.time_end) c.push({ k: 'time', label: `${applied.time_start}–${applied.time_end}` });
    if (applied.booking_type !== 'exclude_bulk') c.push({ k: 'bt', label: BOOKING_TYPES.find((b) => b.value === applied.booking_type)?.label });
    return c;
  }, [applied]);

  const removeChip = (chip) => {
    let next = { ...applied };
    if (chip.k === 'preset') next = { ...next, preset: 'this_month', date_from: '', date_to: '' };
    else if (chip.k === 'time') next = { ...next, time_start: '', time_end: '' };
    else if (chip.k === 'bt') next = { ...next, booking_type: 'exclude_bulk' };
    else if (['venues', 'games', 'days', 'courts', 'platforms'].includes(chip.k)) next[chip.k] = next[chip.k].filter((x) => x !== chip.v);
    setApplied(next); setDraft(next); fetchData(next);
  };

  const k = data?.kpi;
  const trendCfg = {
    revenue: { label: 'Revenue', color: '#059669', fmt: inr },
    bookings: { label: 'Bookings', color: '#0ea5e9', fmt: (v) => v },
    utilisation: { label: 'Utilisation %', color: '#8b5cf6', fmt: (v) => `${v}%` },
    revenue_per_court_hour: { label: 'Rev / Court-Hr', color: '#f59e0b', fmt: inr },
  }[trendMetric];

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="mgmt-dashboard">
        {/* HEADER */}
        <div className="rounded-2xl bg-slate-900 text-white p-6 relative overflow-hidden">
          <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-emerald-500/10" />
          <div className="absolute right-24 -bottom-10 w-32 h-32 rounded-full bg-sky-500/10" />
          <div className="relative">
            <p className="text-emerald-400 text-xs font-semibold uppercase tracking-[0.2em]">MAKS Sports</p>
            <h1 className="text-2xl sm:text-3xl font-bold mt-1">Management Performance Dashboard</h1>
            {data && (
              <p className="text-slate-300 text-sm mt-2">
                {data.period.start} → {data.period.end}
                <span className="text-slate-500"> · compared vs {data.comparison_period.start} → {data.comparison_period.end}</span>
              </p>
            )}
          </div>
        </div>

        {/* FILTER BAR */}
        <Card className="p-4 border-slate-200">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-emerald-600" />
            <span className="font-semibold text-slate-800 text-sm">Global Filters</span>
          </div>
          <div className="flex flex-wrap gap-3 items-end">
            <div className="min-w-[150px]">
              <Label className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Date</Label>
              <Select value={draft.preset} onValueChange={(v) => setDraft({ ...draft, preset: v })}>
                <SelectTrigger className="mt-1" data-testid="mgmt-filter-date"><SelectValue /></SelectTrigger>
                <SelectContent>{DATE_PRESETS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {draft.preset === 'custom' && (
              <>
                <div><Label className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">From</Label>
                  <Input type="date" className="mt-1" value={draft.date_from} onChange={(e) => setDraft({ ...draft, date_from: e.target.value })} data-testid="mgmt-filter-from" /></div>
                <div><Label className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">To</Label>
                  <Input type="date" className="mt-1" value={draft.date_to} onChange={(e) => setDraft({ ...draft, date_to: e.target.value })} data-testid="mgmt-filter-to" /></div>
              </>
            )}
            <MultiSelect label="Venue" testid="venue" allLabel="All Venues" options={(opts.venues || []).map((v) => v.name)} selected={draft.venues} onChange={(v) => setDraft({ ...draft, venues: v, courts: [] })} />
            <MultiSelect label="Game" testid="game" allLabel="All Games" options={opts.games || []} selected={draft.games} onChange={(v) => setDraft({ ...draft, games: v })} />
            <MultiSelect label="Day" testid="day" allLabel="All Days" options={DAYS.map((d) => d.value)} selected={draft.days} onChange={(v) => setDraft({ ...draft, days: v })} />
            <MultiSelect label="Court" testid="court" allLabel="All Courts" options={courtOptions} selected={draft.courts} onChange={(v) => setDraft({ ...draft, courts: v })} />
            <MultiSelect label="Platform" testid="platform" allLabel="All Platforms" options={opts.platforms || []} selected={draft.platforms} onChange={(v) => setDraft({ ...draft, platforms: v })} />
            <div className="min-w-[160px]">
              <Label className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Booking Type</Label>
              <Select value={draft.booking_type} onValueChange={(v) => setDraft({ ...draft, booking_type: v })}>
                <SelectTrigger className="mt-1" data-testid="mgmt-filter-booking-type"><SelectValue /></SelectTrigger>
                <SelectContent>{BOOKING_TYPES.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Time From</Label>
              <Input type="time" className="mt-1 w-[120px]" value={draft.time_start} onChange={(e) => setDraft({ ...draft, time_start: e.target.value })} data-testid="mgmt-filter-time-start" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Time To</Label>
              <Input type="time" className="mt-1 w-[120px]" value={draft.time_end} onChange={(e) => setDraft({ ...draft, time_end: e.target.value })} data-testid="mgmt-filter-time-end" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Peak From</Label>
              <Input type="time" className="mt-1 w-[120px]" value={draft.peak_start} onChange={(e) => setDraft({ ...draft, peak_start: e.target.value })} data-testid="mgmt-filter-peak-start" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Peak To</Label>
              <Input type="time" className="mt-1 w-[120px]" value={draft.peak_end} onChange={(e) => setDraft({ ...draft, peak_end: e.target.value })} data-testid="mgmt-filter-peak-end" />
            </div>
            <div className="flex gap-2">
              <Button onClick={apply} className="bg-emerald-600 hover:bg-emerald-700" data-testid="mgmt-apply-btn">Apply Filters</Button>
              <Button variant="outline" onClick={clearAll} data-testid="mgmt-clear-btn"><RotateCcw className="w-4 h-4 mr-1" />Clear All</Button>
            </div>
          </div>
          {/* chips */}
          <div className="flex flex-wrap gap-2 mt-3">
            {chips.map((chip, i) => (
              <Badge key={i} variant="secondary" className="bg-slate-100 text-slate-700 gap-1 pl-2.5 pr-1.5 py-1" data-testid="mgmt-chip">
                {chip.label}
                {chip.k !== 'preset' && (
                  <button onClick={() => removeChip(chip)} className="hover:text-rose-600"><X className="w-3 h-3" /></button>
                )}
              </Badge>
            ))}
          </div>
        </Card>

        {loading && <div className="text-center py-10 text-slate-400 text-sm">Loading management metrics…</div>}

        {data && k && (
          <>
            {/* KPI ROW */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
              <KpiCard icon={IndianRupee} title="Revenue" value={inr(k.revenue.current)} prev={inr(k.revenue.previous)} change={k.revenue.change} accent="emerald" />
              <KpiCard icon={CalendarCheck} title="Bookings" value={k.bookings.current} prev={k.bookings.previous} change={k.bookings.change} accent="sky" />
              <KpiCard icon={Gauge} title="Utilisation" value={`${k.utilisation.current}%`} prev={`${k.utilisation.previous}%`} change={k.utilisation.change} accent="violet" />
              <KpiCard icon={Clock} title="Booked Hrs" value={k.booked_hours.current} prev={k.booked_hours.previous} change={k.booked_hours.change} accent="amber" />
              <KpiCard icon={Timer} title="Available Hrs" value={k.available_hours.current} prev={k.available_hours.previous} change={k.available_hours.change} accent="slate" />
              <KpiCard icon={Sun} title="Peak Util" value={`${k.peak_utilisation.current}%`} prev={`${k.peak_utilisation.previous}%`} change={k.peak_utilisation.change} accent="orange" />
              <KpiCard icon={Moon} title="Non-Peak Util" value={`${k.nonpeak_utilisation.current}%`} prev={`${k.nonpeak_utilisation.previous}%`} change={k.nonpeak_utilisation.change} accent="indigo" />
              <KpiCard icon={Ticket} title="Avg Rev / Booking" value={inr(k.avg_revenue_per_booking.current)} prev={inr(k.avg_revenue_per_booking.previous)} change={k.avg_revenue_per_booking.change} accent="emerald" />
              <KpiCard icon={Zap} title="Rev / Court-Hr" value={inr(k.revenue_per_court_hour.current)} prev={inr(k.revenue_per_court_hour.previous)} change={k.revenue_per_court_hour.change} accent="rose" />
            </div>

            {/* ROW 2: trends */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="p-4 border-slate-200">
                <div className="flex items-center justify-between mb-3">
                  <SectionTitle sub="Daily, honouring all filters">Revenue Trend</SectionTitle>
                  <Select value={trendMetric} onValueChange={setTrendMetric}>
                    <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="mgmt-trend-metric"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="revenue">Revenue</SelectItem>
                      <SelectItem value="bookings">Bookings</SelectItem>
                      <SelectItem value="utilisation">Utilisation</SelectItem>
                      <SelectItem value="revenue_per_court_hour">Rev / Court-Hr</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={data.trend}>
                    <defs>
                      <linearGradient id="mgTrend" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={trendCfg.color} stopOpacity={0.35} />
                        <stop offset="95%" stopColor={trendCfg.color} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} tickFormatter={(d) => d?.slice(5)} />
                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickFormatter={trendCfg.fmt} width={48} />
                    <Tooltip formatter={(v) => [trendCfg.fmt(v), trendCfg.label]} labelStyle={{ fontSize: 12 }} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Area type="monotone" dataKey={trendMetric} stroke={trendCfg.color} strokeWidth={2} fill="url(#mgTrend)" />
                  </AreaChart>
                </ResponsiveContainer>
              </Card>

              <Card className="p-4 border-slate-200">
                <SectionTitle sub="Booked ÷ Available court-hours">Utilisation Trend</SectionTitle>
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={data.trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} tickFormatter={(d) => d?.slice(5)} />
                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickFormatter={(v) => `${v}%`} width={40} />
                    <Tooltip formatter={(v) => [`${v}%`, 'Utilisation']} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Line type="monotone" dataKey="utilisation" stroke="#8b5cf6" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            </div>

            {/* ROW 3: game perf + peak/nonpeak */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="p-4 border-slate-200 overflow-x-auto">
                <SectionTitle sub="All figures respect active filters">Game Performance</SectionTitle>
                <table className="w-full text-sm" data-testid="mgmt-game-table">
                  <thead><tr className="border-b border-slate-200">
                    <Th>Game</Th><Th right>Revenue</Th><Th right>Bookings</Th><Th right>Util</Th><Th right>Peak</Th><Th right>Rev/Hr</Th><Th right>Δ</Th>
                  </tr></thead>
                  <tbody>
                    {data.game_performance.map((g) => (
                      <tr key={g.game} className="border-b border-slate-100">
                        <Td bold>{g.game}</Td><Td right>{inr(g.revenue)}</Td><Td right>{g.bookings}</Td>
                        <Td right>{g.utilisation}%</Td><Td right>{g.peak_pct}%</Td><Td right>{inr(g.rev_per_court_hour)}</Td>
                        <Td right><ChangePill value={g.change} /></Td>
                      </tr>
                    ))}
                    {data.game_performance.length === 0 && <tr><td colSpan={7} className="text-center py-4 text-slate-400 text-xs">No data</td></tr>}
                  </tbody>
                </table>
              </Card>

              <Card className="p-4 border-slate-200 overflow-x-auto">
                <SectionTitle sub={`Peak = ${data.peak_window.start}–${data.peak_window.end}`}>Peak vs Non-Peak</SectionTitle>
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-slate-200">
                    <Th>Metric</Th><Th right>Peak</Th><Th right>Non-Peak</Th>
                  </tr></thead>
                  <tbody>
                    {[
                      ['Revenue', inr(data.peak_nonpeak.peak.revenue), inr(data.peak_nonpeak.nonpeak.revenue)],
                      ['Bookings', data.peak_nonpeak.peak.bookings, data.peak_nonpeak.nonpeak.bookings],
                      ['Booked Hrs', data.peak_nonpeak.peak.booked_hours, data.peak_nonpeak.nonpeak.booked_hours],
                      ['Available Hrs', data.peak_nonpeak.peak.available_hours, data.peak_nonpeak.nonpeak.available_hours],
                      ['Utilisation', `${data.peak_nonpeak.peak.utilisation}%`, `${data.peak_nonpeak.nonpeak.utilisation}%`],
                      ['Rev / Court-Hr', inr(data.peak_nonpeak.peak.revenue_per_court_hour), inr(data.peak_nonpeak.nonpeak.revenue_per_court_hour)],
                    ].map((r) => (
                      <tr key={r[0]} className="border-b border-slate-100">
                        <Td bold>{r[0]}</Td><Td right>{r[1]}</Td><Td right>{r[2]}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </div>

            {/* ROW 4: venue+court perf + opportunity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="p-4 border-slate-200 overflow-x-auto">
                <SectionTitle>Venue Performance</SectionTitle>
                <table className="w-full text-sm mb-5" data-testid="mgmt-venue-table">
                  <thead><tr className="border-b border-slate-200">
                    <Th>Venue</Th><Th right>Revenue</Th><Th right>Bookings</Th><Th right>Util</Th><Th right>Rev/Hr</Th>
                  </tr></thead>
                  <tbody>
                    {data.venue_performance.map((v) => (
                      <tr key={v.venue} className="border-b border-slate-100">
                        <Td bold>{v.venue}</Td><Td right>{inr(v.revenue)}</Td><Td right>{v.bookings}</Td><Td right>{v.utilisation}%</Td><Td right>{inr(v.rev_per_court_hour)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <SectionTitle sub="Ranked by utilisation">Court Performance</SectionTitle>
                <table className="w-full text-sm" data-testid="mgmt-court-table">
                  <thead><tr className="border-b border-slate-200">
                    <Th>Court</Th><Th>Game</Th><Th right>Booked</Th><Th right>Avail</Th><Th right>Util</Th><Th right>Peak</Th><Th right>Revenue</Th>
                  </tr></thead>
                  <tbody>
                    {data.court_performance.slice(0, 25).map((c) => (
                      <tr key={`${c.venue}-${c.court}`} className="border-b border-slate-100" data-testid={`mgmt-court-row-${c.court}`}>
                        <Td bold>{c.court}<span className="block text-[10px] text-slate-400">{c.venue}</span></Td>
                        <Td>{c.game}</Td>
                        <Td right>{c.booked_hours}</Td>
                        <Td right>{c.available_hours}{c.out_of_hours > 0 && (
                          <span className="block text-[10px] text-amber-600" title="Hours used outside configured working hours (added to available)">+{c.out_of_hours}h out-of-hours</span>
                        )}</Td>
                        <Td right><span className={c.utilisation >= 60 ? 'text-emerald-600 font-semibold' : c.utilisation < 20 ? 'text-rose-600' : ''}>{c.utilisation}%</span></Td>
                        <Td right>{c.peak_pct}%</Td>
                        <Td right>{inr(c.revenue)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>

              <Card className="p-4 border-slate-200">
                <SectionTitle sub="Estimated, not realised revenue">Capacity & Revenue Opportunity</SectionTitle>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {[
                    ['Total Unused', data.opportunity.unused_hours_total, data.opportunity.estimated_revenue_total],
                    ['Peak Unused', data.opportunity.unused_hours_peak, data.opportunity.estimated_revenue_peak],
                    ['Non-Peak Unused', data.opportunity.unused_hours_nonpeak, null],
                  ].map((c) => (
                    <div key={c[0]} className="rounded-xl bg-slate-50 p-3">
                      <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{c[0]}</p>
                      <p className="text-lg font-bold text-slate-900">{c[1]} <span className="text-xs font-normal text-slate-400">hrs</span></p>
                      {c[2] !== null && <p className="text-xs text-emerald-600 font-semibold">~{inr(c[2])} opp.</p>}
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-slate-400 mb-1">Avg realised revenue / court-hour: <span className="font-semibold text-slate-600">{inrFull(data.opportunity.avg_revenue_per_court_hour)}</span></p>
                <div className="overflow-x-auto max-h-64 overflow-y-auto">
                  <table className="w-full text-sm" data-testid="mgmt-opportunity-table">
                    <thead className="sticky top-0 bg-white"><tr className="border-b border-slate-200">
                      <Th>Court</Th><Th right>Unused Hrs</Th><Th right>Est. Revenue</Th>
                    </tr></thead>
                    <tbody>
                      {data.opportunity.by_court.slice(0, 20).map((c) => (
                        <tr key={`${c.venue}-${c.court}`} className="border-b border-slate-100">
                          <Td bold>{c.court}<span className="block text-[10px] text-slate-400">{c.venue}</span></Td>
                          <Td right>{c.unused_hours}</Td><Td right className="text-emerald-600">{inr(c.estimated_revenue)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>

            {/* FOOTER: insights + actions */}
            <Card className="p-5 border-slate-200 bg-slate-900 text-white">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center gap-2 mb-3"><Lightbulb className="w-4 h-4 text-amber-400" /><h3 className="font-bold">Management Summary</h3></div>
                  <ul className="space-y-2" data-testid="mgmt-insights">
                    {(data.insights || []).map((s, i) => (
                      <li key={i} className="text-sm text-slate-200 flex gap-2"><span className="text-emerald-400 mt-0.5">•</span>{s}</li>
                    ))}
                  </ul>
                  {data.integrity && (data.integrity.missing_amount || data.integrity.zero_duration || data.integrity.missing_time || data.integrity.unmatched_court) ? (
                    <p className="mt-3 text-xs text-amber-300 flex items-center gap-1" data-testid="mgmt-integrity-flags"><AlertTriangle className="w-3 h-3" />
                      Data flags — missing amount: {data.integrity.missing_amount}, zero-duration: {data.integrity.zero_duration}, missing time: {data.integrity.missing_time}, unknown courts: {data.integrity.unmatched_court || 0}
                    </p>
                  ) : null}
                  {data.integrity && data.integrity.out_of_hours_hours > 0 ? (
                    <p className="mt-2 text-xs text-amber-300 flex items-start gap-1" data-testid="mgmt-out-of-hours-flag"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                      <span>{data.integrity.out_of_hours_hours} court-hours were booked/blocked outside configured working hours and have been counted as available. Review Working Hours vs Bulk Booking schedules to reconcile.</span>
                    </p>
                  ) : null}
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-3"><Target className="w-4 h-4 text-emerald-400" /><h3 className="font-bold">Recommended Actions</h3></div>
                  <ul className="space-y-2" data-testid="mgmt-actions">
                    {buildActions(data).map((a, i) => (
                      <li key={i} className="text-sm text-slate-200 flex gap-2"><Trophy className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />{a}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

// deterministic recommended actions derived from the filtered result set
function buildActions(data) {
  const out = [];
  const np = data.peak_nonpeak?.nonpeak;
  if (np && np.utilisation < 40) {
    out.push(`Non-peak utilisation is only ${np.utilisation}% — run off-peak offers to fill ${np.available_hours - np.booked_hours} idle court-hours.`);
  }
  const worstCourt = [...(data.court_performance || [])].filter((c) => c.available_hours > 0).sort((a, b) => a.utilisation - b.utilisation)[0];
  if (worstCourt) out.push(`Boost ${worstCourt.court} (${worstCourt.venue}) at ${worstCourt.utilisation}% utilisation via targeted promotion or coaching batches.`);
  const topOpp = (data.opportunity?.by_court || [])[0];
  if (topOpp && topOpp.estimated_revenue > 0) out.push(`Prioritise selling ${topOpp.court} (${topOpp.venue}) — ~${inr(topOpp.estimated_revenue)} untapped opportunity.`);
  const bestGame = [...(data.game_performance || [])].filter((g) => g.rev_per_court_hour > 0).sort((a, b) => b.rev_per_court_hour - a.rev_per_court_hour)[0];
  if (bestGame) out.push(`Allocate more prime-time courts to ${bestGame.game} — it earns the most per court-hour (${inr(bestGame.rev_per_court_hour)}).`);
  if (out.length === 0) out.push('No standout actions for this selection — metrics are balanced across courts and time windows.');
  return out.slice(0, 3);
}
